import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { AbstractPageTemplate } from 'foundations-components/transitional/components/AbstractPageTemplate';
import { Big } from 'foundations-components/transitional/components/Big';
import { Button } from 'foundations-components/transitional/components/Button';
import { Column } from 'foundations-components/transitional/components/Column';
import { ColumnSpreads } from 'foundations-components/transitional/components/ColumnSpreads';
import { ColumnWrapper } from 'foundations-components/transitional/components/ColumnWrapper';
import { List } from 'foundations-components/transitional/components/List';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { Logo } from 'foundations-components/transitional/components/Logo';
import { Section } from 'foundations-components/transitional/components/Section';
import _get from 'hs-lodash/get';
import _isNil from 'hs-lodash/isNil';
import _omitBy from 'hs-lodash/omitBy';
import _times from 'hs-lodash/times';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ACTIONS, SCREENS } from '../../../constants/Tracking';
import { REFRESH_COOKIE_TIMEOUT } from '../../../constants/timeouts';
import { AppContext } from '../../../contexts/app/context';
import { HomePropsContext } from '../../../contexts/home/PropsContext';
import { defaultHomeProps } from '../../../contexts/home/constants';
import useInterval from '../../../hooks/useInterval';
import { prettifyAppName } from '../../../utils/general';
import { getLocalStorageItem, setLocalStorageItem } from '../../../utils/localStorage';
import MeetingInfo from '../MeetingInfo';
import { IconContainer } from './Home.style';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NUMBER_OF_FEATURES = 4;
function Home() {
  const {
    usageTracker,
    repeatedVisitKey,
    meetingContext,
    isLoading,
    unauthorizedRoutesParam,
    accountsRoute,
    fetchUserInfo,
    ctaButtonText,
    ctaFunction
  } = useContext(HomePropsContext);
  const {
    appName
  } = useContext(AppContext);
  const history = useHistory();
  const isRepeatedVisit = getLocalStorageItem(repeatedVisitKey);
  const params = new URLSearchParams(history.location.search);
  const autoRedirect = params.get(unauthorizedRoutesParam);
  const needToTrackEvents = useMemo(() => !isRepeatedVisit || !!autoRedirect, [autoRedirect, isRepeatedVisit]);
  useInterval(fetchUserInfo, REFRESH_COOKIE_TIMEOUT, {
    forceRefresh: true,
    cancelOnUnmount: true
  });
  useEffect(() => {
    if (needToTrackEvents && !ctaFunction) {
      usageTracker.trackPageView(SCREENS.login);
    }
  }, [needToTrackEvents, usageTracker, ctaFunction]);
  const startApp = useCallback(() => {
    if (ctaFunction) {
      ctaFunction();
      return;
    }
    if (needToTrackEvents) {
      usageTracker.trackInteraction(Object.assign({
        screen: SCREENS.login
      }, ACTIONS.login.start));
    }
    if (!isRepeatedVisit) {
      setLocalStorageItem(repeatedVisitKey, '1');
    }
    history.push(accountsRoute);
  }, [needToTrackEvents, isRepeatedVisit, history, usageTracker, repeatedVisitKey, accountsRoute, ctaFunction]);
  useEffect(() => {
    if (!isLoading && isRepeatedVisit && !autoRedirect && !ctaFunction) {
      startApp();
    }
  }, [startApp, isRepeatedVisit, isLoading, autoRedirect, ctaFunction]);
  if ((isLoading || isRepeatedVisit) && !autoRedirect && !ctaFunction) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {});
  }
  return /*#__PURE__*/_jsxs(AbstractPageTemplate, {
    "data-test-id": `${appName}-home-page`,
    headerComponent: /*#__PURE__*/_jsx(View, {
      marginBlockStart: "300",
      marginBlockEnd: "0",
      marginInlineEnd: "auto",
      marginInlineStart: "auto",
      children: /*#__PURE__*/_jsx(Logo, {
        name: "hubspot-wordmark",
        width: "100%",
        height: 30
      })
    }),
    horizontalDivider: false,
    mainSectionProps: {
      mainStyles: {
        paddingBlockEnd: '0'
      }
    },
    children: [/*#__PURE__*/_jsxs(Section, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "home.title",
          options: {
            appName: prettifyAppName(appName)
          }
        })
      }), /*#__PURE__*/_jsx(Big, {
        use: "help",
        children: /*#__PURE__*/_jsx(Text, {
          as: "small",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "home.subTitle"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(Section, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "home.featuresTitle",
          options: {
            appName: prettifyAppName(appName)
          }
        })
      }), /*#__PURE__*/_jsx(List, {
        wrapChildren: true,
        children: _times(NUMBER_OF_FEATURES, n => n + 1).map(featureNumber => /*#__PURE__*/_jsxs(ColumnWrapper, {
          align: "center",
          marginBlockEnd: "200",
          children: [/*#__PURE__*/_jsx(Column, {
            children: /*#__PURE__*/_jsx(IconContainer, {
              children: /*#__PURE__*/_jsx(SuccessIcon, {
                size: "100",
                color: "fill-surface-default"
              })
            })
          }), /*#__PURE__*/_jsx(ColumnSpreads, {
            children: /*#__PURE__*/_jsx(View, {
              paddingInline: "200",
              children: /*#__PURE__*/_jsx(Text, {
                as: "small",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `home.feature_${featureNumber}`
                })
              })
            })
          })]
        }, featureNumber))
      })]
    }), meetingContext.meetingID && /*#__PURE__*/_jsxs(Section, {
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h5",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "home.callTitle"
        })
      }), /*#__PURE__*/_jsx(MeetingInfo, {
        meetingID: _get(meetingContext, 'meetingID', ''),
        meetingTopic: _get(meetingContext, 'meetingTopic', '')
      })]
    }), /*#__PURE__*/_jsx(Section, {
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(Button, {
        use: "primary",
        onClick: startApp,
        "data-test-id": `${appName}-start-button`,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: ctaButtonText || 'home.start.button'
        })
      })
    })]
  });
}
const WrappedHome = props => {
  const definedProps = _omitBy(props, _isNil);
  return /*#__PURE__*/_jsx(HomePropsContext.Provider, {
    value: Object.assign({}, defaultHomeProps, definedProps),
    children: /*#__PURE__*/_jsx(Home, {})
  });
};
export default WrappedHome;